import React, { useState, useEffect, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ErrorMessage } from '@hookform/error-message';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import intlTelInput from 'intl-tel-input';
import IntlTelInput from 'intl-tel-input/react';
import _ from 'lodash';
import Error from 'Components/Error';
import s from './Field.module.scss';

const Field = ({
    type,
    name,
    id,
    defaultValue,
    label,
    placeholder,
    required,
    tooltip,
    modifier,
    errors,
    error,
    register,
    onChange,
    onBlur,
    labelSrOnly,
    width,
    maxLength = null,
    formMethods = {},
}) => {
    const fieldId = _.isEmpty(id) ? name : id;

    const classes = classNames(
        s['Field'],
        {[s['Field--Error']]: errors[name] || error},
        {[s[`Field--${modifier}`]]: modifier},
        {[s['Field--Required']]: required},
        {[s[`Field--${width}`]]: width},
    );

    const labelClass = labelSrOnly ? 'sr-only' : s['Field__Label'];

    return (
        <div className={classes}>
            {tooltip &&
                <ReactTooltip
                    id={`tooltip-${fieldId}`}
                    className={s['Field__ReactTooltip']}
                    place="top"
                    type="dark"
                    effect="solid"
                    multiline={true}
                />
            }
            <label
                htmlFor={fieldId}
                className={labelClass}>
                {label}
                {tooltip &&
                    <span data-tooltip-id={`tooltip-${fieldId}`} data-tooltip-content={tooltip} className={s['Field__Tooltip']} />
                }
            </label>
            {type === 'tel' ? (
                <PhoneInput
                    type={type}
                    className={s['Field__Field']}
                    name={name}
                    id={fieldId}
                    placeholder={placeholder}
                    defaultValue={defaultValue}
                    maxLength={maxLength}
                    required={required}
                    register={register}
                    {...formMethods}
                />
            ) : (
                <input
                    type={type}
                    className={s['Field__Field']}
                    name={name}
                    id={fieldId}
                    placeholder={placeholder}
                    defaultValue={defaultValue}
                    onBlur={onBlur}
                    maxLength={maxLength || undefined}
                    {...register}
                />
            )}
            <ErrorMessage
                errors={errors}
                name={name}
                as={<Error />}
            />
        </div>
    );
};

Field.propTypes = {
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    defaultValue: PropTypes.string,
    label: PropTypes.string,
    id: PropTypes.string,
    placeholder: PropTypes.string,
    required: PropTypes.bool, // Only for cosmetics
    tooltip: PropTypes.string,
    modifier: PropTypes.string,
    errorMessage: PropTypes.string,
    errors: PropTypes.object,
    error: PropTypes.bool,
    labelSrOnly: PropTypes.bool,
    register: PropTypes.func,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    width: PropTypes.string,
    formMethods: PropTypes.object,
};

Field.defaultProps = {
    type: 'text',
    name: '',
    defaultValue: '',
    label: '',
    id: '',
    placeholder: null,
    required: false,
    tooltip: '',
    modifier: '',
    errorMessage: '',
    errors: {},
    error: false,
    register: null,
    labelSrOnly: false,
    onChange: null,
    onBlur: null,
    width: '',
};

const PhoneInput = ({
    type = 'tel',
    className = '',
    name = '',
    id = '',
    placeholder = '',
    defaultValue = '',
    maxLength = '',
    required = false,
    register,
    setValue = () => {},
    setError = () => {},
    clearErrors = () => {},
    ...restProps
}) => {
    const inputRef = useRef(null);
    const itiRef = useRef(null);

    const initOptions = {
        initialCountry: 'se',
        countryOrder: ['se'],
        onlyCountries: ['se'],
        countrySearch: false,
        allowDropdown: false,
        separateDialCode: true,
        validationNumberTypes: null, // Allowing land line numbers
        loadUtils: () => import('intl-tel-input/utils'),
    };

    const countryChange = () => {
        changeFields();
    };

    const changeFields = () => {
        const isValid = itiRef.current.isValidNumber();
        const number = itiRef.current.getNumber();
        setValue(name, number, { shouldValidate: false, shouldDirty: true });
        if(isValid || (!required && number.length === 0)) {
            clearErrors(name);
        } else {
            setError(name, {
                type: 'manual',
                message: 'Vänligen fyll i ett giltigt telefonnummer: 073 123 45 67. Endast svenska telefonnummer är tillåtna',
            });
        }
    };

    const blurHandler = (e) => {
        const val = e.currentTarget.value;
        changeFields();
    };

    const changeHandler = (e) => {
        changeFields();
    };

    useEffect(() => {
        // store a reference to the current input ref, which otherwise is already lost in the cleanup function
        if(inputRef && inputRef.current && itiRef) {
            const inputRefCurrent = inputRef.current;
            const initIti = async () => {
                const sv = (await import('intl-tel-input/i18n/sv')).default;
                const instance = intlTelInput(inputRefCurrent, {
                    ...initOptions,
                    i18n: sv,
                });
                itiRef.current = instance;
                inputRefCurrent.addEventListener('countrychange', countryChange);
            };

            initIti();
            return () => {
                inputRefCurrent.removeEventListener('countrychange', countryChange);
                itiRef.current.destroy();
            };
        }
    }, []);

    return (
        <input
            type={type}
            className={className}
            name={name}
            id={id}
            placeholder={placeholder}
            defaultValue={defaultValue}
            maxLength={maxLength || undefined}
            {...register} // Includes ref, name, onBlur and onChange
            ref={inputRef}
            onBlur={(e) => blurHandler(e)}
            onChange={(e) => changeHandler(e)}
        />
    );
};

export default Field;
